import type { ForecastSummaryMock } from '@/lib/mock-data/forecast-summary';
import type { ForecastPerformance } from '../types';

function formatSnapshotDate(summary: ForecastSummaryMock | null): string | null {
  if (!summary) return null;
  try {
    return new Date(summary.lastSnapshotDate).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch {
    return summary.lastSnapshotDate;
  }
}

/**
 * Derives the Forecast Performance headline figures and supporting rows from a
 * forecast snapshot. Supporting rows fall back to zeroes when there is no
 * snapshot; the cards render "—" in that case.
 */
export function buildForecastPerformance(summary: ForecastSummaryMock | null): ForecastPerformance {
  const hasData = summary !== null;

  const forecastMarginGbp = hasData
    ? summary.totalForecastRevenue - summary.totalForecastCost
    : 0;
  const forecastMarginPercent = hasData && summary.totalForecastRevenue > 0
    ? (forecastMarginGbp / summary.totalForecastRevenue) * 100
    : 0;

  const rows = {
    totalBudgetCost:        285000,
    actualsToDate:          142000,
    forecastToComplete:     hasData ? summary.totalForecastCost - 142000 : 0,
    costVariance:           hasData ? 285000 - summary.totalForecastCost : 0,
    costVariancePercent:    hasData ? ((285000 - summary.totalForecastCost) / 285000) * 100 : 0,
    actualInvoiced:         148000,
    revenueVariance:        -12000,
    revenueVariancePercent: -3.9,
    forecastEndDate:        '2027-06-30',
    budgetMarginPercent:    18.2,
    marginVariance:         forecastMarginPercent - 18.2,
  };

  const marginBadge =
    forecastMarginPercent >= 15
      ? { label: 'Healthy', cls: 'bg-success-100 text-success-700' }
      : forecastMarginPercent >= 5
        ? { label: 'At Risk', cls: 'bg-warning-100 text-warning-700' }
        : { label: 'Critical', cls: 'bg-error-100 text-error-700' };

  return {
    summary,
    forecastMarginGbp,
    forecastMarginPercent,
    rows,
    costUnderBudget: rows.costVariance > 0,
    revenuePositive: rows.revenueVariance >= 0,
    marginBadge,
    lastUpdatedFormatted: formatSnapshotDate(summary),
  };
}
